import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '../../api/client';
import { idempotency } from '../shop/api';

export type SupportChat = Schemas['SupportChat'];
export type SupportMessage = Schemas['SupportMessage'];
export type SendSupportMessage = Schemas['SendSupportMessageRequest'];

export const supportKeys = {
  chat: ['support', 'chat'] as const,
  unread: ['support', 'unread'] as const,
};

/** The shop's conversation; checked every 3 s while it is on screen so replies show up at once. */
export function useSupportChat(enabled = true) {
  return useQuery({
    queryKey: supportKeys.chat,
    queryFn: () => unwrap(api.GET('/support/chat', { params: { query: { limit: 100 } } })),
    enabled,
    refetchInterval: 3_000,
  });
}

/** Unread replies for the Support menu badge (checked every 30 s). */
export function useSupportUnread(enabled = true) {
  return useQuery({
    queryKey: supportKeys.unread,
    queryFn: () => unwrap(api.GET('/support/chat/unread')),
    enabled,
    refetchInterval: 30_000,
    select: (d) => d.unread,
  });
}

export function useMarkSupportRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/support/chat/read')),
    onSuccess: () => qc.setQueryData(supportKeys.unread, { unread: 0 }),
  });
}

export function useSendSupportMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SendSupportMessage) =>
      unwrap(api.POST('/support/chat/messages', { params: idempotency(), body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: supportKeys.chat }),
  });
}

/** Older messages before the oldest one shown. */
export function fetchOlder(before: string) {
  return unwrap(api.GET('/support/chat', { params: { query: { before, limit: 100 } } }));
}
